import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  auth, 
  db,
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  googleProvider, 
  signInWithPopup,
  sendEmailVerification,
  sendPasswordResetEmail,
  onAuthStateChanged,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp
} from '../services/firebase';

// Create the authentication context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider component to wrap the app and provide auth functionality
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Sign up with email and password
  const signup = async (email, password, displayName) => {
    try {
      setError('');
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Send email verification
      await sendEmailVerification(userCredential.user);
      
      // Store user data in Firestore
      const userRef = doc(db, 'users', userCredential.user.uid);
      await setDoc(userRef, {
        uid: userCredential.user.uid,
        email: email,
        displayName: displayName,
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
        emailVerified: false,
        tools: []
      });
      
      return userCredential.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Sign in with email and password
  const login = async (email, password) => {
    try {
      setError('');
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Update last login timestamp
      const userRef = doc(db, 'users', userCredential.user.uid);
      
      try {
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          await updateDoc(userRef, {
            lastLogin: serverTimestamp(),
            emailVerified: userCredential.user.emailVerified
          });
        } else {
          // Create user document if it doesn't exist
          await setDoc(userRef, {
            uid: userCredential.user.uid,
            email: userCredential.user.email,
            displayName: userCredential.user.displayName || '',
            createdAt: serverTimestamp(),
            lastLogin: serverTimestamp(),
            emailVerified: userCredential.user.emailVerified,
            tools: []
          });
        }
      } catch (firestoreErr) {
        console.error('Error updating Firestore user:', firestoreErr);
      }
      
      return userCredential.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Sign in with Google
  const loginWithGoogle = async () => {
    try {
      setError('');
      const result = await signInWithPopup(auth, googleProvider);
      
      // Store or update user data in Firestore
      const userRef = doc(db, 'users', result.user.uid);
      
      // Check if the user document already exists
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        // Update existing user
        await updateDoc(userRef, {
          lastLogin: serverTimestamp(),
          emailVerified: true // Google accounts are pre-verified
        });
      } else {
        // Create new user document
        await setDoc(userRef, {
          uid: result.user.uid,
          email: result.user.email,
          displayName: result.user.displayName || '',
          createdAt: serverTimestamp(),
          lastLogin: serverTimestamp(),
          emailVerified: true, // Google accounts are pre-verified
          tools: []
        });
      }
      
      return result.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Sign out
  const logout = () => {
    return signOut(auth);
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      setError('');
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Resend verification email
  const verifyEmail = async () => {
    try {
      setError('');
      if (currentUser && !currentUser.emailVerified) {
        await sendEmailVerification(currentUser);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };
  
  // Update user's Firestore record when email is verified
  const updateEmailVerificationStatus = async (user) => {
    if (user && user.emailVerified) {
      const userRef = doc(db, 'users', user.uid);
      try {
        await updateDoc(userRef, {
          emailVerified: true
        });
      } catch (error) {
        console.error('Error updating email verification status:', error);
      }
    }
  };

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Force refresh the user to get latest email verification status
        await user.reload();
        // Update Firestore if email is verified
        await updateEmailVerificationStatus(user);
      }
      
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Context value
  const value = {
    currentUser,
    signup,
    login,
    loginWithGoogle,
    logout,
    resetPassword,
    verifyEmail,
    error,
    setError,
    updateEmailVerificationStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};